import { useState, useEffect, useRef } from 'react';

// Optimized Image component with lazy loading and blur placeholder
export const OptimizedImage = ({ 
    src, 
    alt, 
    className = '',
    width,
    height,
    priority = false,
    quality = 80
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(priority);
    const imgRef = useRef(null);

    useEffect(() => {
        if (priority) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '100px' }
        );

        if (imgRef.current) {
            observer.observe(imgRef.current);
        }

        return () => observer.disconnect();
    }, [priority]);

    // Generate optimized URL with quality parameter
    const optimizedSrc = src?.includes('unsplash.com') 
        ? `${src}&q=${quality}&auto=format&fit=crop`
        : src;

    return (
        <div 
            ref={imgRef}
            className={`relative overflow-hidden ${className}`}
            style={{ width, height }}
        >
            {/* Blur placeholder */}
            <div 
                className={`absolute inset-0 bg-gradient-to-br from-muted to-muted/50 transition-opacity duration-500 ${
                    isLoaded ? 'opacity-0' : 'opacity-100'
                }`}
            />
            
            {isInView && (
                <img
                    src={optimizedSrc}
                    alt={alt}
                    width={width}
                    height={height}
                    loading={priority ? 'eager' : 'lazy'}
                    decoding="async"
                    onLoad={() => setIsLoaded(true)}
                    className={`w-full h-full object-cover transition-opacity duration-500 ${
                        isLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                />
            )}
        </div>
    );
};

// Skeleton loader for content
export const Skeleton = ({ className = '', variant = 'text' }) => {
    const baseClass = 'animate-pulse bg-gradient-to-r from-muted via-muted/70 to-muted bg-[length:200%_100%]';
    
    const variants = {
        text: 'h-4 rounded',
        title: 'h-8 rounded',
        avatar: 'rounded-full',
        card: 'rounded-2xl',
        image: 'rounded-xl aspect-video',
    };

    return (
        <div className={`${baseClass} ${variants[variant]} ${className}`} />
    );
};

// Content loader for pages
export const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            <p className="text-muted-foreground animate-pulse">Loading...</p>
        </div>
    </div>
);

// Intersection observer hook for animations
export const useInView = (options = {}) => {
    const ref = useRef(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    if (!options.repeat) {
                        observer.disconnect();
                    }
                } else if (options.repeat) {
                    setIsInView(false);
                }
            },
            { threshold: options.threshold || 0.1, rootMargin: options.rootMargin || '0px' }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [options.threshold, options.rootMargin, options.repeat]);

    return [ref, isInView];
};

// Animated section wrapper
export const AnimatedSection = ({ children, className = '', delay = 0 }) => {
    const [ref, isInView] = useInView({ threshold: 0.1 });

    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ${className}`}
            style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateY(0)' : 'translateY(30px)',
                transitionDelay: `${delay}ms`
            }}
        >
            {children}
        </div>
    );
};

// Preload critical images
export const preloadImage = (src) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
};

// Resource hints for performance
export const addResourceHints = () => {
    // Preconnect to image CDN
    const preconnects = [
        'https://images.unsplash.com',
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com'
    ];

    preconnects.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = url;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
    });
};
